import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, Mail, LogOut, Shield, CheckCircle, XCircle, Trash2, Send } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ onLogout }) => {
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'waitlist', or 'admins'
  const [admins, setAdmins] = useState([]);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [grantAccessImmediately, setGrantAccessImmediately] = useState(true);
  const [selectedWaitlistIds, setSelectedWaitlistIds] = useState([]);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingEmail, setConvertingEmail] = useState('');
  const [convertingId, setConvertingId] = useState('');
  const [convertUsername, setConvertUsername] = useState('');
  const [convertPassword, setConvertPassword] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [usersRes, waitlistRes, adminsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, config),
        axios.get(`${API_URL}/api/admin/waitlist`, config),
        axios.get(`${API_URL}/api/admin/admins`, config)
      ]);

      setUsers(usersRes.data);
      setWaitlist(waitlistRes.data);
      setAdmins(adminsRes.data);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserAccess = async (userId, currentAccess) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/access?access_granted=${!currentAccess}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setUsers(users.map(u => 
        u.id === userId ? { ...u, access_granted: !currentAccess } : u
      ));
    } catch (err) {
      setError('Failed to update user access');
      console.error(err);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    }
  };

  const resendWelcomeEmail = async (user) => {
    // For regular users, username IS their email
    const userEmail = user.username;
    
    if (!window.confirm(`Resend welcome email with new password to ${userEmail}?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      setLoading(true);
      await axios.post(
        `${API_URL}/api/admin/resend-welcome-email`,
        { email: userEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Welcome email with new temporary password sent successfully to ${userEmail}`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to resend welcome email';
      setError(errorMsg);
      console.error(err);
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const deleteWaitlistEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this waitlist entry?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${API_URL}/api/admin/waitlist/${entryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWaitlist(waitlist.filter(e => e.id !== entryId));
    } catch (err) {
      setError('Failed to delete waitlist entry');
      console.error(err);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // First register the user (email is the username)
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        username: newUsername,
        password: newPassword
      });

      const token = localStorage.getItem('admin_token');

      // If grantAccessImmediately is true, grant access right away
      if (grantAccessImmediately) {
        await axios.put(
          `${API_URL}/api/admin/users/${response.data.id}/access?access_granted=true`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        response.data.access_granted = true;
      }

      // Send welcome email with credentials
      try {
        await axios.post(
          `${API_URL}/api/admin/send-welcome-email`,
          {
            email: newUsername,  // Email is the username
            password: newPassword
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Welcome email sent successfully');
      } catch (emailErr) {
        console.error('Failed to send welcome email:', emailErr);
        // Don't fail the entire operation if email fails
      }

      // Add to users list
      setUsers([...users, response.data]);
      
      // Show success message
      alert(`User created successfully!\n\nWelcome email sent to: ${newUsername}\n\nPassword: ${newPassword}\n\nMake sure to save these credentials.`);
      
      // Reset form and close modal
      setNewUsername('');
      setNewPassword('');
      setShowAddUserModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const convertWaitlistToUser = async (email, entryId) => {
    // Set up the modal with email
    setConvertingEmail(email);
    setConvertingId(entryId);
    // Pre-fill username with email prefix
    const username = email.split('@')[0];
    setConvertUsername(username);
    setConvertPassword('');
    setShowConvertModal(true);
  };

  const generatePassword = () => {
    // Generate a secure random password
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    if (showConvertModal) {
      setConvertPassword(newPassword);
    } else if (showAddUserModal) {
      setNewPassword(newPassword);
    }
  };

  const handleConvertToUser = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // For regular users, use email as username
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        username: convertingEmail,  // Email IS the username for regular users
        password: convertPassword
      });

      // Grant access immediately
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API_URL}/api/admin/users/${response.data.id}/access?access_granted=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      response.data.access_granted = true;

      // Add to users list
      setUsers([...users, response.data]);

      // Send welcome email with credentials
      try {
        await axios.post(
          `${API_URL}/api/admin/send-welcome-email`,
          {
            email: convertingEmail,
            password: convertPassword
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Welcome email sent successfully');
      } catch (emailErr) {
        console.error('Failed to send welcome email:', emailErr);
        // Don't fail the entire operation if email fails
      }

      // Remove from waitlist
      await axios.delete(
        `${API_URL}/api/admin/waitlist/${convertingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWaitlist(waitlist.filter(e => e.id !== convertingId));

      // Show success message
      alert(`User created successfully!\n\nCredentials have been sent to: ${convertingEmail}\n\nEmail: ${convertingEmail}\nPassword: ${convertPassword}\n\nMake sure to save these credentials.`);

      // Reset and close modal
      setConvertUsername('');
      setConvertPassword('');
      setConvertingEmail('');
      setConvertingId('');
      setShowConvertModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to convert to user');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedWaitlistIds.length === waitlist.length) {
      setSelectedWaitlistIds([]);
    } else {
      setSelectedWaitlistIds(waitlist.map(e => e.id));
    }
  };

  const toggleSelectWaitlist = (id) => {
    if (selectedWaitlistIds.includes(id)) {
      setSelectedWaitlistIds(selectedWaitlistIds.filter(wid => wid !== id));
    } else {
      setSelectedWaitlistIds([...selectedWaitlistIds, id]);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedWaitlistIds.length === 0) return;
    
    if (!window.confirm(`Delete ${selectedWaitlistIds.length} waitlist entries?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      
      // Delete all selected entries
      await Promise.all(
        selectedWaitlistIds.map(id =>
          axios.delete(`${API_URL}/api/admin/waitlist/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );

      // Update state
      setWaitlist(waitlist.filter(e => !selectedWaitlistIds.includes(e.id)));
      setSelectedWaitlistIds([]);
    } catch (err) {
      setError('Failed to delete some entries');
      console.error(err);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API_URL}/api/admin/create-admin`,
        {
          username: newAdminUsername,
          password: newAdminPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Reload admins
      const response = await axios.get(`${API_URL}/api/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);

      // Reset and close
      setNewAdminUsername('');
      setNewAdminPassword('');
      setShowAddAdminModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const deleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/admin/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAdmins(admins.filter(a => a.id !== adminId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const viewUserAnalytics = async (userId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAnalytics(response.data);
      setSelectedUserId(userId);
    } catch (err) {
      setError('Failed to load user analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchConvert = async () => {
    if (selectedWaitlistIds.length === 0) return;

    if (!window.confirm(`Convert ${selectedWaitlistIds.length} waitlist entries to users? Random passwords will be generated and sent via email.`)) return;

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    try {
      const token = localStorage.getItem('admin_token');
      const selectedEntries = waitlist.filter(e => selectedWaitlistIds.includes(e.id));

      for (const entry of selectedEntries) {
        try {
          // Generate secure random password for each user
          const randomPassword = generatePassword();

          // Register user - email IS the username for regular users
          const response = await axios.post(`${API_URL}/api/auth/register`, {
            username: entry.email,  // Email as username
            password: randomPassword
          });

          // Grant access
          await axios.put(
            `${API_URL}/api/admin/users/${response.data.id}/access?access_granted=true`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Send welcome email
          try {
            await axios.post(
              `${API_URL}/api/admin/send-welcome-email`,
              {
                email: entry.email,
                password: randomPassword
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (emailErr) {
            console.error(`Failed to send email to ${entry.email}:`, emailErr);
          }

          // Delete from waitlist
          await axios.delete(`${API_URL}/api/admin/waitlist/${entry.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (err) {
          console.error(`Failed to convert ${entry.email}:`, err);
          failCount++;
        }
      }

      // Reload data
      await loadData();
      setSelectedWaitlistIds([]);
      
      alert(`Converted ${successCount} entries successfully. ${failCount > 0 ? `${failCount} failed.` : ''}\n\nWelcome emails with credentials have been sent to all users.`);
    } catch (err) {
      setError('Batch conversion failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Admin Dashboard</h1>
              <p className="text-sm text-slate-600">Speaker Notes CMS</p>
            </div>
          </div>
          <Button onClick={onLogout} variant="outline" className="gap-2">
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          <button
            onClick={() => {
              setActiveTab('users');
              setSelectedUserId(null);
              setUserAnalytics(null);
            }}
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'users'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users ({users.length})
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab('waitlist');
              setSelectedUserId(null);
              setUserAnalytics(null);
            }}
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'waitlist'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Waitlist ({waitlist.length})
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab('admins');
              setSelectedUserId(null);
              setUserAnalytics(null);
            }}
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'admins'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Admins ({admins.length})
            </div>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>
                    Grant or revoke access to registered users
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setShowAddUserModal(true)}
                  className="gap-2"
                >
                  <Users className="w-4 h-4" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-semibold text-slate-700">Email</th>
                      <th className="pb-3 font-semibold text-slate-700">Registered</th>
                      <th className="pb-3 font-semibold text-slate-700">Access</th>
                      <th className="pb-3 font-semibold text-slate-700">Analytics</th>
                      <th className="pb-3 font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-slate-500">
                          No users registered yet
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-slate-50">
                          <td className="py-4 font-medium">{user.username}</td>
                          <td className="py-4 text-slate-600">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                                user.access_granted
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {user.access_granted ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  Granted
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-3 h-3" />
                                  Denied
                                </>
                              )}
                            </span>
                          </td>
                          <td className="py-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewUserAnalytics(user.id)}
                              className="gap-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              View Analytics
                            </Button>
                          </td>
                          <td className="py-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={user.access_granted ? "outline" : "default"}
                                onClick={() => toggleUserAccess(user.id, user.access_granted)}
                              >
                                {user.access_granted ? 'Revoke' : 'Grant'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resendWelcomeEmail(user)}
                                className="gap-1"
                                title="Resend welcome email with new password"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteUser(user.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Waitlist Tab */}
        {activeTab === 'waitlist' && (
          <Card>
            <CardHeader>
              <CardTitle>Waitlist Management</CardTitle>
              <CardDescription>
                View and manage waitlist email addresses
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Batch Actions */}
              {waitlist.length > 0 && (
                <div className="mb-4 flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedWaitlistIds.length === waitlist.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      {selectedWaitlistIds.length > 0
                        ? `${selectedWaitlistIds.length} selected`
                        : 'Select all'}
                    </span>
                  </div>
                  {selectedWaitlistIds.length > 0 && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleBatchConvert}
                        disabled={loading}
                        className="gap-2"
                      >
                        <Users className="w-4 h-4" />
                        Convert to Users
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleBatchDelete}
                        disabled={loading}
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Selected
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 w-12"></th>
                      <th className="pb-3 font-semibold text-slate-700">Email Address</th>
                      <th className="pb-3 font-semibold text-slate-700">Joined</th>
                      <th className="pb-3 font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="py-8 text-center text-slate-500">
                          No waitlist entries yet
                        </td>
                      </tr>
                    ) : (
                      waitlist.map((entry) => (
                        <tr key={entry.id} className="border-b hover:bg-slate-50">
                          <td className="py-4">
                            <input
                              type="checkbox"
                              checked={selectedWaitlistIds.includes(entry.id)}
                              onChange={() => toggleSelectWaitlist(entry.id)}
                              className="w-4 h-4 rounded border-slate-300"
                            />
                          </td>
                          <td className="py-4 font-medium">{entry.email}</td>
                          <td className="py-4 text-slate-600">
                            {new Date(entry.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => convertWaitlistToUser(entry.email, entry.id)}
                                className="gap-1"
                              >
                                <Users className="w-4 h-4" />
                                Add as User
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteWaitlistEntry(entry.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>Admin Management</CardTitle>
                  <CardDescription>
                    Manage admin accounts
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => setShowAddAdminModal(true)}
                  className="gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Add Admin
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-semibold text-slate-700">Username</th>
                      <th className="pb-3 font-semibold text-slate-700">Created</th>
                      <th className="pb-3 font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="py-8 text-center text-slate-500">
                          No admins found
                        </td>
                      </tr>
                    ) : (
                      admins.map((admin) => (
                        <tr key={admin.id} className="border-b hover:bg-slate-50">
                          <td className="py-4 font-medium">{admin.username}</td>
                          <td className="py-4 text-slate-600">
                            {new Date(admin.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteAdmin(admin.id)}
                              disabled={admins.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {admins.length === 1 && (
                  <p className="text-xs text-slate-500 mt-4">
                    Note: Cannot delete the last admin account
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Analytics View */}
        {selectedUserId && userAnalytics && (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>User Analytics: {userAnalytics.user.username}</CardTitle>
                  <CardDescription>
                    Session and presentation analytics
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedUserId(null);
                    setUserAnalytics(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-slate-600 mb-1">Total Presentations</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {userAnalytics.summary.total_presentations}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-slate-600 mb-1">Total Sessions</p>
                  <p className="text-3xl font-bold text-green-600">
                    {userAnalytics.summary.total_sessions}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-slate-600 mb-1">Avg. Session Time</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {userAnalytics.summary.average_session_time_minutes}m
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-sm text-slate-600 mb-1">Peak Concurrent</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {userAnalytics.summary.peak_concurrent_sessions}
                  </p>
                </div>
              </div>

              {/* Presentations Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-semibold text-slate-700">Presentation</th>
                      <th className="pb-3 font-semibold text-slate-700">Total Sessions</th>
                      <th className="pb-3 font-semibold text-slate-700">Active Sessions</th>
                      <th className="pb-3 font-semibold text-slate-700">Avg. Time</th>
                      <th className="pb-3 font-semibold text-slate-700">Peak Concurrent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userAnalytics.presentations.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-slate-500">
                          No presentations yet
                        </td>
                      </tr>
                    ) : (
                      userAnalytics.presentations.map((pres) => (
                        <tr key={pres.presentation_id} className="border-b hover:bg-slate-50">
                          <td className="py-4 font-medium">{pres.presentation_title}</td>
                          <td className="py-4 text-slate-600">{pres.total_sessions}</td>
                          <td className="py-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              {pres.active_sessions} active
                            </span>
                          </td>
                          <td className="py-4 text-slate-600">{pres.average_session_time}m</td>
                          <td className="py-4 text-slate-600">{pres.peak_concurrent}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Add New User</CardTitle>
              <CardDescription>
                Create a new user account. A welcome email with credentials will be sent.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <Label htmlFor="new-username">Email</Label>
                  <Input
                    id="new-username"
                    type="email"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Enter user's email"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">Password</Label>
                  <div className="flex gap-2">
                    <Input
                      id="new-password"
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGeneratePassword}
                    >
                      Generate
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Click &quot;Generate&quot; to create a secure random password
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="grant-access"
                    checked={grantAccessImmediately}
                    onChange={(e) => setGrantAccessImmediately(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <Label htmlFor="grant-access" className="cursor-pointer">
                    Grant access immediately
                  </Label>
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                    {error}
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddUserModal(false);
                      setNewUsername('');
                      setNewPassword('');
                      setError('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Adding...' : 'Add User'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Convert Waitlist to User Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Convert to User</CardTitle>
              <CardDescription>
                Create user account from: <span className="font-semibold">{convertingEmail}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConvertToUser} className="space-y-4">
                <div>
                  <Label htmlFor="convert-username">Username</Label>
                  <Input
                    id="convert-username"
                    value={convertUsername}
                    onChange={(e) => setConvertUsername(e.target.value)}
                    placeholder="Enter username"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Pre-filled from email address
                  </p>
                </div>
                <div>
                  <Label htmlFor="convert-password">Password</Label>
                  <div className="flex gap-2">
                    <Input
                      id="convert-password"
                      type="text"
                      value={convertPassword}
                      onChange={(e) => setConvertPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGeneratePassword}
                    >
                      Generate
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Click &quot;Generate&quot; to create a secure random password
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700">
                  <strong>Note:</strong> Access credentials will be automatically sent to the user&apos;s email address.
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                    {error}
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowConvertModal(false);
                      setConvertUsername('');
                      setConvertPassword('');
                      setConvertingEmail('');
                      setConvertingId('');
                      setError('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Converting...' : 'Create User'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Add New Admin</CardTitle>
              <CardDescription>
                Create a new admin account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddAdmin} className="space-y-4">
                <div>
                  <Label htmlFor="admin-username">Username</Label>
                  <Input
                    id="admin-username"
                    value={newAdminUsername}
                    onChange={(e) => setNewAdminUsername(e.target.value)}
                    placeholder="Enter admin username"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={newAdminPassword}
                    onChange={(e) => setNewAdminPassword(e.target.value)}
                    placeholder="Enter admin password"
                    required
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                    {error}
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddAdminModal(false);
                      setNewAdminUsername('');
                      setNewAdminPassword('');
                      setError('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Adding...' : 'Add Admin'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
